import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Alert, Button, Nav } from 'react-bootstrap';
import { Link, useParams, useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '../../shared/DateFormatContext';
import { usePermissions } from '../../shared/permissions';
import { useQueryClient } from '@tanstack/react-query';
import { useProject, projectKeys } from '../../entities/project';

// Import sub-components
import ProjectInfo from './components/ProjectInfo';
import ProjectStaff from './components/ProjectStaff';
import ProjectVehicles from './components/ProjectVehicles';
import ProjectOffers from './components/ProjectOffers';
import ProjectTasks from './components/ProjectTasks';
import ProjectEquipment from './components/ProjectEquipment';
import ProjectFiles from './components/ProjectFiles';
import ProjectCases from './components/ProjectCases';
import ProjectNotes from './components/ProjectNotes';
import { useConfirm } from '../../shared/ConfirmContext';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import StatusBadge from '../../components/ui/StatusBadge';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [convertingToContract, setConvertingToContract] = useState(false);

  // Derive the active tab from the URL path segment after the projectId
  const TAB_KEYS = ['info', 'items', 'cases', 'offers', 'staff', 'vehicles', 'tasks', 'files', 'notes'];
  const urlSegment = location.pathname.split('/').filter(Boolean).pop();
  const activeTab = TAB_KEYS.includes(urlSegment) ? urlSegment : 'info';

  const [actionError, setActionError] = useState(null);

  // Use a ref so the blocker function always reads the latest value synchronously,
  // even when navigate() is called before the batched setIsDirty(false) re-renders.
  const isDirtyRef = useRef(false);
  const [isDirty, _setIsDirty] = useState(false);
  const setIsDirty = useCallback((val) => {
    isDirtyRef.current = val;
    _setIsDirty(val);
  }, []);

  // Block full-page navigation (sidebar, back button) when unsaved changes exist
  const blocker = useBlocker(() => isDirtyRef.current);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      confirm(t('common.unsavedChangesWarning')).then(ok => {
        if (ok) {
          isDirtyRef.current = false;
          _setIsDirty(false);
          blocker.proceed();
        } else {
          blocker.reset();
        }
      });
    }
  }, [blocker.state]);

  const { data: project, isLoading: loading, error: queryError } = useProject(projectId);
  useEffect(() => { if (project) setActionError(null); }, [project]);

  const refreshProject = () => queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });

  const { formatDate: fmt } = useDateFormat();
  const formatDate = (dateString) => {
    if (!dateString) return t('common.notSet');
    return fmt(dateString);
  };

  const tabItems = [
    { key: 'info', label: t('projects.menu.projectInfo'), icon: 'file-text' },
    { key: 'items', label: t('projects.menu.items'), icon: 'package' },
    { key: 'cases', label: t('projects.menu.cases'), icon: 'archive' },
    { key: 'staff', label: t('projects.menu.staff'), icon: 'users' },
    { key: 'vehicles', label: t('projects.menu.vehicles'), icon: 'truck' },
    { key: 'notes', label: t('projects.menu.notes'), icon: 'notes' },
    { key: 'offers', label: t('projects.menu.offers'), icon: 'file-dollar' },
    { key: 'tasks', label: t('projects.menu.tasks'), icon: 'checklist' },
    { key: 'files', label: t('projects.menu.files'), icon: 'folder' },
  ];

  const handleTabSelect = async (key) => {
    if (isDirtyRef.current) {
      const ok = await confirm(t('common.unsavedChangesWarning'));
      if (!ok) return;
      isDirtyRef.current = false;  // synchronous — blocker won't fire on navigate()
      _setIsDirty(false);
    }
    navigate(`/projects/detail/${projectId}/${key}`, { replace: true });
  };

  if (loading) {
    return (
      <PageShell
        pretitle={t('projects.overview')}
        title={t('projects.title')}
        description={t('projects.subtitle')}
      >
        <PageSection>
          <CenteredLoader />
        </PageSection>
      </PageShell>
    );
  }

  if (queryError) {
    return (
      <PageShell
        pretitle={t('projects.overview')}
        title={t('projects.title')}
        description={t('projects.subtitle')}
      >
        <PageSection>
          <Alert variant="danger" className="mb-0">{t('projects.detail.errorLoadingDetails')}</Alert>
        </PageSection>
      </PageShell>
    );
  }

  if (!project) {
    return (
      <PageShell
        pretitle={t('projects.overview')}
        title={t('projects.title')}
        description={t('projects.subtitle')}
      >
        <PageSection>
          <EmptyState
            title={t('projects.detail.projectNotFound')}
            action={(
              <Button as={Link} to="/projects" variant="outline-secondary">
                {t('projects.detail.backToProjects')}
              </Button>
            )}
          />
        </PageSection>
      </PageShell>
    );
  }

  // Render the appropriate component based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return <ProjectInfo project={project} onUpdate={refreshProject} />;
      case 'items':
        return <ProjectEquipment projectId={projectId} onDirtyChange={setIsDirty} defaultTimeUnits={project?.billed_days ?? 1} projectStartDate={project.start_date} projectEndDate={project.end_date} />;
      case 'cases':
        return <ProjectCases projectId={projectId} />;
      case 'staff':
        return <ProjectStaff projectId={projectId} onDirtyChange={setIsDirty} projectStartDate={project.start_date} projectEndDate={project.end_date} />;
      case 'vehicles':
        return <ProjectVehicles projectId={projectId} onDirtyChange={setIsDirty} projectStartDate={project.start_date} projectEndDate={project.end_date} />;
      case 'offers':
        return <ProjectOffers projectId={projectId} projectDiscount={project.discount ?? 0} />;
      case 'tasks':
        return <ProjectTasks projectId={projectId} projectStartDate={project.start_date} projectEndDate={project.end_date} />;
      case 'files':
        return <ProjectFiles projectId={projectId} />;
      case 'notes':
        return <ProjectNotes projectId={projectId} projectName={project?.name} />;
      default:
        return null;
    }
  };

  return (
    <PageShell
      pretitle={t('projects.overview')}
      title={project.name}
      description={(
        <div className="d-flex flex-wrap align-items-center gap-2">
          {project.status?.name ? (
            <StatusBadge color={project.status?.color || '#206bc4'} label={project.status.name} />
          ) : null}
          <span className="text-muted">
            {t('projects.detail.projectNumber')}{project.id} | {t('projects.detail.created')}: {formatDate(project.created_at)}
          </span>
        </div>
      )}
      actions={(
        <>
          <Button
            as={Link}
            to="/projects"
            variant="outline-secondary"
          >
            <i className="ti ti-arrow-left me-2" aria-hidden="true" />
            {t('projects.detail.backToProjects')}
          </Button>
          {canManage && (
            <>
              <Button
                variant="warning"
                disabled={convertingToContract}
                onClick={async () => {
                  if (await confirm(
                    t('projects.detail.convertToContractConfirmation')
                  )) {
                    setConvertingToContract(true);
                    try {
                      const result = await api.post('contracts/api/contracts/create_from_project/', {
                        project_id: parseInt(projectId, 10),
                      });
                      navigate(`/contracts/contract/${result.contract.id}`);
                    } catch (err) {
                      const msg = err?.response?.data?.error || err?.message || t('projects.detail.convertError');
                      setActionError(msg);
                      toast.error(msg);
                      setConvertingToContract(false);
                    }
                  }
                }}
              >
                <i className="ti ti-file-invoice me-2" aria-hidden="true" />
                {convertingToContract ? t('projects.detail.converting') : t('projects.detail.convertToContract')}
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  if (await confirm(t('projects.detail.deleteConfirmation'))) {
                    api.delete(`projects/api/projects/${projectId}/`)
                      .then(() => navigate('/projects'))
                      .catch((deleteError) => {
                        console.error('Error deleting project:', deleteError);
                        setActionError(t('projects.detail.deleteError'));
                        toast.error(t('projects.detail.deleteError'));
                      });
                  }
                }}
              >
                <i className="ti ti-trash me-2" aria-hidden="true" />
                {t('projects.detail.deleteProject')}
              </Button>
            </>
          )}
        </>
      )}
    >
      <PageSection bodyClassName="pt-3">
        {actionError ? <Alert variant="danger">{actionError}</Alert> : null}

        <Nav className="project-tabnav-mobile" activeKey={activeTab}>
          {tabItems.map((tab) => (
            <Nav.Link
              key={tab.key}
              as="button"
              eventKey={tab.key}
              active={activeTab === tab.key}
              onClick={() => handleTabSelect(tab.key)}
            >
              <i className={`ti ti-${tab.icon}`} aria-hidden="true" />
              {tab.label}
            </Nav.Link>
          ))}
        </Nav>

        <div className="pt-4" key={`${projectId}-${activeTab}`}>
          {renderTabContent()}
        </div>
      </PageSection>
    </PageShell>
  );
};

export default ProjectDetail;
